<template>
    <div class="routes-result">
        <div class="routes-result__modes sidebar__section sidebar__section--border-top-huge sidebar__section--border-bottom sidebar__section--order-0">
            <ItemSlider slider-class="routes-result__modes__slider" :slider-options="modeSliderOptions" ref="nav-slider" :tooltips="tooltipArrowsContent">
                <SwiperSlide 
                    class="routes-result__modes__item" 
                    v-for="mode in availableModes" 
                    :key="mode.key"
                    :ref="isNavLinkActive(mode.key) ? 'active-nav-link' : 'nav-link'"
                >
                    <SmrTooltip :content="tooltipContent(mode.key) ? tooltipContent(mode.key) : ''" :position="tooltipContent(mode.key) ? 'bottom' : ''">
                        <div class="routes-result__mode" :class="{'is-active' : isNavLinkActive(mode.key), 'is-loading': mode.loading && mode.selected}" @click="switchActiveMode(mode.key)">
                            <div class="routes-result__mode__icon">
                                <Icon :height="18" :width="18">
                                    <TransportModeIcon :mode="mode.key"/>
                                </Icon>
                            </div>
                            <div class="routes-result__mode__duration chips" v-if="mode.duration">{{mode.duration === 'shortest' ? shortestRouteTime : mode.duration | formatDuration(false) }}</div>
                            <!-- <Loader class="routes-result__mode__loader" v-if="mode.loading && mode.selected"/> -->
                        </div>
                    </SmrTooltip>
                </SwiperSlide>
            </ItemSlider>
        </div>

        <template v-if="availableRoutes">
            <div class="sidebar__section sidebar__section--border-top sidebar__section--border-bottom" v-if="showNoResults">
                <NoResultsMessage :face="activeTransportModeTab === 'LIST'" :icon="activeTransportModeTab !== 'LIST'" :type="activeTransportModeTab" :system-error-type="systemErrorType">
                    <TransportModeIcon :mode="activeTransportModeTab" slot="icon"/>
                </NoResultsMessage>
            </div>

            <div class="sidebar__section--ordered" ref="resultsContainer">
                <RouteResultItem 
                    class="order-1"
                    v-for="(route, index) in availableRoutes" :key="route.id + activeTransportModeTab"
                    :class="{'is-active' : route.id === activeRouteId}"
                    :active="route.id === activeRouteId"
                    :route="route"
                    :ref="getResultItemRef(route, index)"
                    :list-tab="isListTab"
                    @click.native="routeClickHandler(route)"
                    @actionClickHandler="action = true"
                />

                <ContentLoader :class="`sidebar__section order-${contentLoaderPosition}`" v-if="activeTransportModeLoading" />
            </div>

            <!-- earlier / later buttons for public transport -->
            <div class="routes-result__navigation sidebar__section" v-if="showPublicTransportSwitches">
                <div class="routes-result__navigation__button" :class="{'disabled' : option.disabled}" v-for="option in navigationOptions" :key="option.value" @click="loadRouteByTimeType(option.value)">
                    <SmrTooltip :content="option.tooltip.content" :position="option.tooltip.position">
                        <div class="routes-result__navigation__button__circle">
                            <Icon :width="16" :height="16">
                                <component :is="option.icon" />
                            </Icon>
                        </div>
                    </SmrTooltip>
                    <div class="routes-result__navigation__button__label caption">{{ option.title }}</div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import * as api from '@/api';
import { formatDuration } from '@/utils/format'
import { getListResultRouteData, getOverallPolyline, formatRouteTimeForPoi } from '@/utils/route-utils'
import { updateQueryParams } from '@/utils/url-utils'
import transportModes from "@data/transportModes"
import { trackEvent, trackGa4Event } from '@/utils/tracking'
import eventBus from '@/utils/event-bus';
import TransportModeIcon from '@/components/TransportModeIcon.vue'
import ItemSlider from '@/components/ItemSlider.vue'
import RouteResultItem from '@/components/RouteResultItem.vue'
import ContentLoader from '@/components/ContentLoader.vue'
import SmrTooltip from '@/components/SmrTooltip.vue'
// import Loader from '@/components/Loader.vue'
import Icon from '@/components/Icon.vue'
import IconChevronLeft from '@components/icons/IconChevronLeft.vue';
import IconChevronRight from '@components/icons/IconChevronRight.vue';
import IconChevronRightDouble from '@components/icons/IconChevronRightDouble.vue';
import IconChevronLeftDouble from '@components/icons/IconChevronLeftDouble.vue';
import { HAS_PUBLIC_TRANSPORT, TRAFFIC_TRANSPORT_MODES } from '@/data/transportModesTypes';
import { getPOIResultByType} from '@/utils/poi-utils'
import { getPoiConfig } from '@/utils/poi-data/poi-config'
import { getPoiParams } from '@/utils/poi-params';

export default {
    components: {
        ItemSlider,
        NoResultsMessage: () => import(/* webpackChunkName: "NoResultsMessage" */ '@/components/NoResultsMessage.vue'),
        RouteResultItem,
        ContentLoader,
        // Loader,
        SmrTooltip,
        TransportModeIcon,
        Icon,
        IconChevronLeft,
        IconChevronRight,
        IconChevronRightDouble,
        IconChevronLeftDouble
    },
    props: {
        waypointCount: {
            type: Number,
            default: 2,
        }
    },
    filters: {
        formatDuration(duration, addSpaces = true) {
            return formatDuration(duration, addSpaces)
        },
    },
    data() {
        return {
            firstRoute: false,
            lastRoute: false,
            routesByTimeLoading: false,
            lastActiveRoutesForMode: {},
            modeSliderOptions: {
                slidesPerView: 'auto',
                spaceBetween: 6,
                slideToClickedSlide: false,
                centeredSlides: true,
                centeredSlidesBounds: true,
                watchOverflow: true,
                touchStartPreventDefault: false,
                freeMode: true,
                freeModeMomentumRatio: 0.5
            },
            navigationOptions: [
                {
                    title: 'Erste',
                    value: 'FIRST',
                    icon: 'IconChevronLeftDouble',
                    disabled: false,
                    tooltip: {
                        content: 'Erste Verbindung des Tages',
                        position: 'bottom'
                    },
                },
                {
                    title: 'Früher',
                    value: 'EARLIER',
                    icon: 'IconChevronLeft',
                    disabled: false,
                    tooltip: {
                        content: 'Frühere Verbindungen',
                        position: 'bottom'
                    },
                },
                {
                    title: 'Später',
                    value: 'LATER',
                    icon: 'IconChevronRight',
                    disabled: false,
                    tooltip: {
                        content: 'Später Verbindungen',
                        position: 'bottom'
                    },
                },
                {
                    title: 'Letzte',
                    value: 'LAST',
                    icon: 'IconChevronRightDouble',
                    disabled: false,
                    tooltip: {
                        content: 'Letzte Verbindung des Tages',
                        position: 'bottom'
                    },
                },
            ],
            tooltipArrowsContent: {
                left: {
                    content: 'Weitere Transportmittel',
                    position: 'top'
                },
                right: {
                    content: 'Weitere Transportmittel',
                    position: 'right'
                }
            },
            action: false,
        }
    },
    computed: {
        ...mapGetters(['mapGlSupported', 'transportModes', 'selectedTransportModeKeys', 'routesLoading', 'activeRouteId', 'activeTransportModeTab', 'routeTrafficTime', 'routeSettingsGeneralTypes', 'routeTimeDateFormatted', 'routeCorridor', 'transportModeFromUrl', 'savedRouteClicked', 'detailRouteId', 'trafficPOIselected', 'routeSettings', 'savedRouteSettings', 'savedRouteSettingsActive']),
        availableModes() {
            let modes = [];
            
            // Add transport modes in order from the route settings
            let selectedModesCount = this.routeSettingsGeneralTypes.length;
            let unselectedIndex = selectedModesCount;
            for (const modeKey in this.transportModes) {
                let modeIndex = this.routeSettingsGeneralTypes.indexOf(modeKey);
                if(modeIndex < 0) {
                    modeIndex = unselectedIndex;
                    unselectedIndex++;
                }
                modes[modeIndex] = this.createTransportModeConfig(modeKey);
            }
            modes.unshift({...this.createTransportModeConfig('LIST'), loading: this.routesLoading, selected: true, duration: 'shortest'});
            return modes
        },
        shortestRouteTime() {
            let durations = [];
            this.availableModes.forEach(mode => {
                if(Number.isInteger(mode.duration)) {
                    durations.push(mode.duration);
                }
            })
            return Math.min(...durations);
        },
        availableRoutes() {
            let availableRoutes = [];
            if(this.activeTransportModeTab === 'LIST') {
                let fastestRoutes = [];
                for (const modeKey in this.transportModes) {
                    if(this.transportModes[modeKey].routes.length) {
                        let fastestRoute = this.transportModes[modeKey].routes[0];
                        fastestRoute.mode = modeKey;
                        fastestRoutes.push(getListResultRouteData(fastestRoute));
                    }
                }
                fastestRoutes.sort((a, b) => {
                    return a.durationUnformatted - b.durationUnformatted;
                });
                availableRoutes = fastestRoutes;
                this.interimStopMessageAlert(availableRoutes);
            } else {
                if(this.transportModes[this.activeTransportModeTab].routes.length) {
                    let previousRoute = false
                    this.transportModes[this.activeTransportModeTab].routes.forEach(route => {
                        let formattedRoute = getListResultRouteData(route)
                        // for public transport modes on different days than the selected one, show a headline in between
                        if(this.activeTransportModeTab === 'PUBLIC') {
                            if(
                                (previousRoute && ((formattedRoute.departureDate !== previousRoute.departureDate) || previousRoute.isLastOfDay || previousRoute.isFirstOfDay)) || 
                                (!previousRoute && formattedRoute.departureDate !== this.routeTimeDateFormatted)
                            ) {
                                formattedRoute.showDateHeadline = true
                            }
                        }
                        availableRoutes.push(formattedRoute)
                        previousRoute = formattedRoute
                    })
                }
            }

            return availableRoutes;
        },
        showPublicTransportSwitches() {
            return this.activeTransportModeTab === 'PUBLIC' && !this.showNoResults;
        },
        activeTransportModeLoading() {
            if(!this.availableModes.length || !this.activeTransportModeTab) return false;
            const activeMode = this.availableModes.filter(mode => mode.key === this.activeTransportModeTab);
            if(!activeMode.length) return false;
            return activeMode[0].loading;
        },
        contentLoaderPosition() {
            if(this.routesByTimeLoading === 'FIRST' || this.routesByTimeLoading === 'EARLIER') return '0'
            return '2'
        },
        showNoResults() {
            return !this.activeTransportModeLoading && !this.availableRoutes.length;
        },
        isListTab() {
            return this.activeTransportModeTab === 'LIST'
        },
        systemErrorType() {
            if(this.transportModes[this.activeTransportModeTab]) {
                return this.transportModes[this.activeTransportModeTab].error
            } else { // Eg. LIST
                let error = 500;
                for (const modeKey in this.transportModes) {
                    if(this.transportModes[modeKey].selected && !this.transportModes[modeKey].error) {
                        error = null
                    }
                }
                return error;
            }
        },
    },
    watch: {
        activeTransportModeLoading(val) {
            if(!val && this.activeRouteId === '') {
                this.setFirstRouteActive();
            }
        },
        activeTransportModeTab(val) {
            this.activateRouteForMode();
            updateQueryParams(this.$route, { transportMode: val, routeOptions: this.savedRouteSettingsActive ? this.savedRouteSettings : this.routeSettings })
        },
        activeRouteId(newVal) {
            this.storeLastActiveRouteForMode(newVal);
            // load traffic messages for active route
            const activeRoute = newVal ? this.availableRoutes.find(r => r.id === newVal) : false
            if(activeRoute && !activeRoute.fitBounds) {
                this.loadRouteTraffic()
            } else {
                this.removeRouteTraffic()
            }
        },
        trafficPOIselected(newVal, oldVal) {
            // load traffic messages again if traffic poi gets deactivated
           if(oldVal && !newVal) this.loadRouteTraffic()
        }
    },
    created() {
        if(this.transportModeFromUrl) {
            this.switchActiveMode(this.transportModeFromUrl)
            this.$store.commit('setTransportModeFromUrl', false)
        } else if(!this.savedRouteClicked) {
            this.setActiveTab();
        }
        if(this.activeRouteId === '' && !this.activeTransportModeLoading) {
            this.setFirstRouteActive();
        }

        // this.$store.commit('setSavedRouteClicked', false)

        this.addListeners()
    },
    updated() {
        this.scrollToActiveTab()
    },
    beforeDestroy() {
        this.removeListeners()
        if(!this.activeRouteId && !this.detailRouteId) {
            this.removeRouteTraffic()
        }
    },
    methods: {
        addListeners() {
            eventBus.$on('setRouteActiveById', this.setRouteActiveByIdHandler)
            eventBus.$on('itemClosed', this.closeDetailsHandler)
            eventBus.$on('loadTrafficResults', this.loadRouteTraffic)
        },
        removeListeners() {
            eventBus.$off('setRouteActiveById', this.setRouteActiveByIdHandler)
            eventBus.$off('itemClosed', this.closeDetailsHandler)
            this.$store.commit('setSavedRouteClicked', false) // set saved routes click to false
        },
        closeDetailsHandler() {
            this.action = false;
        },
        setActiveTab() {
            if (!this.activeTransportModeTab) {
                this.$store.commit('setActiveTransportModeTab', this.availableModes[1].key) // Show first tab as active initially
            }
        },
        switchActiveMode(mode) {
            this.trackingEvent(mode);
            this.$store.commit('setActiveTransportModeTab', mode)
            this.firstRoute = false
            this.lastRoute = false
            if(mode !== 'LIST' && !this.selectedTransportModeKeys.includes(mode)) {
                this.deactivateCurrentRoute();
                this.$store.dispatch('loadRouteResultsByType', { type: mode });
            }
        },
        trackingEvent(mode) {
            let transportMode = {};
            if(mode === 'LIST') transportMode.trackingLabel = 'Compare means of transport';
            else transportMode = transportModes.filter(item => item.value === mode)[0];
            trackEvent({ category: 'Route Planner', action: 'Mean of Transport', label: transportMode.trackingLabel })  
            const trackingValue = transportMode.trackingLabel.replace(/ /g, "_").toLowerCase();
            trackGa4Event({ga4Parameter1Name: 'route', ga4Parameter1Value: `transport-${trackingValue}`, ga4Event: 'route_planner'})  
        },
        tooltipContent(mode) {
            let content = false;
            switch(mode) {
                case 'LIST' :
                    content = 'Verkehrsmittel-Vergleich';
                break;
                case 'CAR' :
                    content = 'Auto';
                break;    
                case 'PUBLIC' :
                    content = 'Öffentlicher Verkehr';
                break; 
                case 'FOOT' :
                    content = 'Fußgänger';
                break; 
                case 'BIKE' :
                    content = 'Fahrrad';
                break;
                case 'MOTORCYCLE':
                    content = 'Motorrad';
                    break;
                case 'CAMPER' :
                    content = 'Wohnmobil';
                break;
                case 'TRAILER' :
                    content = 'Gespann';
                break;
                case 'PARK_AND_RIDE':
                    content = 'Park & Ride'
                    break;
                case 'BIKE_AND_RIDE':
                    content = 'Bike & Ride'
                    break;
                case 'SHARING':
                    content = 'Sharing'
                    break;
            }
            return content;
        },
        routeClickHandler(route){
            if(!this.action) {
                trackEvent({ category: 'Route Planner', action: 'Route', label: 'Preview' })
                // trackGa4Event({ga4Parameter1Name: 'route', ga4Parameter1Value: 'select_alternative_routes', ga4Event: 'route_planner'})
            }
               
            this.setRouteActive(route);
        },
        setRouteActiveByIdHandler(routeId) {
            this.setRouteActiveById(routeId)
        },
        setRouteActiveById(routeId) {
            const routeToActivate = this.availableRoutes.find(route => route.id === routeId)
            if(routeToActivate) {
                this.setRouteActive(routeToActivate)
                // also update detail route if detail view is open
                if(this.detailRouteId) {
                    this.$store.dispatch('setDetailRoute', {id: routeToActivate.id, mode: routeToActivate.mode});
                }
            }
        },
        setRouteActive(route) {
            if(this.activeRouteId === route.id) {
                // we need to redraw all routes for static maps for stacking issues
                if(this.mapGlSupported) {
                    eventBus.$emit('removeRoutesFromMap', route.id);
                    this.showAdditionalRoutes(route.id)
                    return false
                }
            }
            eventBus.$emit('removeRoutesFromMap'); // always clear all routes when new ones are drawn
            this.showAdditionalRoutes(route.id)
            this.$store.commit('setActiveRouteId', route.id);
            this.$store.commit('setActiveRouteMode', route.mode);
            if(!route.polylines || !route.polylines.length) return false;

            let overallPolyline = getOverallPolyline(route.polylines);
            this.$store.commit('setRouteCorridor', overallPolyline);
            eventBus.$emit('showRouteOnMap', {route, type: 'primary', fitBounds: route.fitBounds}); // primary type is the main one showed on the map, secondary ones are grey out

            // in case route detail is already open, udpate it
            if(this.detailRouteId) {
                this.$store.dispatch('setDetailRoute', {id: route.id, mode: route.mode});
            }
        },
        deactivateCurrentRoute() {
            this.$store.commit('setActiveRouteId', '');
            this.$store.commit('setActiveRouteMode', false);
            this.$store.commit('setRouteCorridor', false);
            eventBus.$emit('removeRoutesFromMap');
        },
        setFirstRouteActive() {
            if(!this.availableRoutes.length) return false;
            this.setRouteActive(this.availableRoutes[0]);
        },
        showAdditionalRoutes(activeRouteId) {
            if(this.activeTransportModeTab === 'LIST' || HAS_PUBLIC_TRANSPORT.includes(this.activeTransportModeTab) || this.availableRoutes.length <= 1) return false
            this.availableRoutes.forEach(route => {
               if(route.id !== activeRouteId) {
                    eventBus.$emit('showRouteOnMap', {route, type: 'secondary'});
               }
           })
        },
        isNavLinkActive(mode) {
            return this.activeTransportModeTab === mode;
        },
        scrollToActiveTab() {
            const activeLink = this.$refs['active-nav-link'][0];
            const activeLinkLeft = activeLink.$el.offsetLeft;
            const activeLinkWidth = activeLink.$el.clientWidth;
            const navSlider = this.$refs['nav-slider'];
            const navSliderWidth = navSlider.$el.clientWidth; 
            if(activeLinkLeft + activeLinkWidth > navSliderWidth) {
                const position = activeLinkLeft - 40;
                navSlider.slideToPosition(-position);
            }
        },
        createTransportModeConfig(modeKey) {
            let duration = false
            if(this.transportModes[modeKey] && this.transportModes[modeKey].routes.length && this.transportModes[modeKey].selected) {
                if(modeKey === 'PUBLIC') duration = this.transportModes[modeKey].routes[0].metadata.duration //checks if it's public transport then pics the first one as earliest arrival
                else duration = Math.min(...this.transportModes[modeKey].routes.map(item => item.metadata.duration))
                // duration = Math.min(...this.transportModes[modeKey].routes.map(item => item.metadata.duration))
            }
            return {
                ...this.transportModes[modeKey],
                key: modeKey,
                duration
            };
        },
        async loadRouteByTimeType(timeType) {
            this.routesByTimeLoading = timeType

            if(timeType === 'FIRST' || timeType === 'EARLIER') {
                setTimeout(() => {
                    eventBus.$emit('scrollToSidebar', {target: this.$refs.resultsContainer})
                }, 10);
            }

            await this.$store.dispatch('loadRouteResultsByTimeType', timeType);
            this.routesByTimeLoading = false
            if(timeType === 'FIRST') {
                this.navigationOptions.find(option => {return option.value === timeType}).disabled = true
            } else if(timeType === 'LAST') {
                this.navigationOptions.find(option => {return option.value === timeType}).disabled = true
            } 
            setTimeout(() => {
                let resultElement = this.$refs.resultItem0.length > 0 ? this.$refs.resultItem0 : this.$refs.resultItem1
                if(timeType === 'FIRST') {
                    resultElement = this.$refs.resultItemFirst
                } else if(timeType === 'LAST') {
                    resultElement = this.$refs.resultItemLast
                } else if(timeType === 'LATER') {
                    resultElement = this.$refs[`resultItem${[this.availableRoutes.length - 3]}`]
                }

                eventBus.$emit('scrollToSidebar', {target: resultElement[0].$el})
            }, 200);
        },
        activateRouteForMode() {
            if(this.activeTransportModeTab === 'LIST') {
                // If the currently active route is not present on the LIST overview - use the first one
                if(!this.getRouteById(this.activeRouteId)){
                    this.setFirstRouteActive();
                } else {
                    eventBus.$emit('removeRoutesFromMap', this.activeRouteId);
                }
            } else {
                if(this.lastActiveRoutesForMode[this.activeTransportModeTab]) {
                    let route = this.getRouteById(this.lastActiveRoutesForMode[this.activeTransportModeTab]);
                    if(route) {
                        this.setRouteActive(route)
                    } else {
                        this.setFirstRouteActive();
                    }
                } else {
                    this.setFirstRouteActive();
                }
            }
        },
        storeLastActiveRouteForMode(routeId) {
            if(this.activeTransportModeTab === 'LIST') return false;
            this.lastActiveRoutesForMode[this.activeTransportModeTab] = routeId;
        },
        getRouteById(routeId) {
            let route = this.availableRoutes.find(route => route.id === routeId);
            if(!route) return false;
            return route;
        },
        getResultItemRef(route, index) {
            if(route.isFirstOfDay) return 'resultItemFirst'
            if(route.isLastOfDay) return 'resultItemLast'
            return `resultItem${index}`
        },

        interimStopMessageAlert(data) {
           let index = data.findIndex(item => item.mode === 'PUBLIC');
           if(this.waypointCount > 2 && data[index]) {
                Object.assign(data[index], {interimStopMessageAlert: true});
           } 
        },
        async loadRouteTraffic() {
            if(this.trafficPOIselected) return
            const type = 'traffic';
            eventBus.$emit('removePoisFromMap', {type})
            const activeRoute = this.getRouteById(this.activeRouteId)
            if(!activeRoute || !TRAFFIC_TRANSPORT_MODES.includes(activeRoute.mode)) return
            const apiType = getPoiConfig(type).apiType;
            const params = getPoiParams(type);
            if(!params.type) params.type = {}
            params.type.values = ['Baustelle', 'Schneeketten', 'Sperre', 'Wintersperre', 'Störung', 'Verkehr', 'Stau', 'Straßenzustand'];
            if(!this.routeTrafficTime.isNow) {
                params.time = formatRouteTimeForPoi(this.routeTrafficTime)
            }
            // park & ride should only show traffic for the first section
            if(activeRoute.mode === 'PARK_AND_RIDE') {
                params.geographic.corridor.polylineWKT = activeRoute.polylines[0].polyline
            }
            if(!params.geographic.corridor) params.geographic.corridor = {}
            params.geographic.corridor.radius = 0.15
            try {
                const responseMap = await api.poi.getPOIsForMapByType(apiType, params);
                const dataMap = getPOIResultByType(responseMap, type);
                eventBus.$emit('showPoisOnMap', {type, data: dataMap})
            } catch(e) {
                console.error(e);
            }
        },
        removeRouteTraffic() {
            if(this.trafficPOIselected) return
            const type = 'traffic';
            eventBus.$emit('removePoisFromMap', {type})
        }
    }
}
</script>

<style lang="scss">
.routes-result {
    $block: &;
    overflow: hidden;

    display: flex;
    flex-direction: column;

    &__modes {
        position: relative;
        &__slider {
            padding: 10px 0;
        }

        &__item {
            width: auto;
        }
    }
    &__mode {
        transition: background-color 0.15s;
        display: flex;
        align-items: center;
        background: $color-black-005;
        padding: 8px 9px;
        border-radius: $button-border-radius;
        cursor: pointer;
        position: relative;

        .no-touch &:hover {
            background-color: $color-black-01;
        }

        &.is-active {
            background: $color-yellow !important;
            cursor: default;

            #{$block}__mode__icon {
                color: $color-black;
            }
        }
        &.is-loading {
            // padding-right: 30px;
            background-color: $color-black-003;
            padding: 8px 20px;

            #{$block}__mode__icon {
                color: $color-grey-silver;
            }

            &.is-active {
                background-color: $color-black-003 !important;
            }
        }

        &__icon {
            line-height: 0;
            color: $color-grey-dark;
            margin-right: 5px;
            &:last-child {
                margin-right: 0;
            }
        }

        &__duration {
            &:not(:last-child) {
                margin-right: 8px;
            }
        }

        &__loader {
            position: absolute;
            right: 8px;
            top: 50%;
            transform: translateY(-50%);
        }
    }

    &__subtitle {
        color: $color-black-05;
        padding-top: 8px;
        padding-bottom: 8px;

        &--spacious {
            padding-top: 24px;
        }
    }

    &__navigation {
        display: flex;
        justify-content: center;
        margin: 16px 0;

        &__button {
            text-align: center;
            margin: 0 12px;
            cursor: pointer;

            .no-touch &:hover {
                #{$block}__navigation__button__circle {
                    background: $color-black-01;
                }
            }

            &.disabled {
                pointer-events: none;
                #{$block}__navigation__button__circle {
                    opacity: 0.5;
                }
            }

            &__circle {
                width: 32px;
                height: 32px;
                @include flex-center;
                border-radius: 100%;
                background: $color-black-005;
                color: $color-grey-dark;
                margin-bottom: 2px;
                transition: background 0.15s;
            }
            &__label {
                color: $color-black-05;
            }
        }
    }
}
</style>